import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FlexBoxFullWidthCenter } from 'styles';
import { useMeInfo } from 'hooks';
import { useQuerySubscription } from 'hooks/useQuerySubscription';
import { useRobotOrderDispatch } from 'hooks/useRobotDelivery';
import { IConfirmOrder, IRobotSpot, IRobotOrderSubscribe } from 'interfaces/robot';
import { getStoreHost } from 'apis/host';

import DefaultModal from '../components/DefaultModal';
import DefaultTable, { TColumns } from '../components/DefaultTable';
import PrinterLightIcon from '../components/ph_printer-light.svg?react';

const AggregateModalWrapper = styled(FlexBoxFullWidthCenter)`
  padding: 45px 45px 0;
  .blue {
    color: #0067ff;
  }
  .description {
    color: #6d7582;
    font-size: 20px;
    text-align: center;
    white-space: pre;
    padding-top: 30px;
  }
  .red {
    color: #fa503c;
  }
  .align {
    text-align: center;
  }
  .loading-empty {
    content: '';
    width: 100%;
    height: 182px;
  }
  .error {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    white-space: break-spaces;
    font-size: 24px;
    color: #fa503c;
  }
`;
const ModalHeaderWrapper = styled.div`
  text-align: center;
  font-size: 32px;
  font-weight: 700;
`;
const ModalContentWrapper = styled.div`
  width: 100%;
  font-size: 20px;
  font-weight: 700;
  color: #6d7582;

  .deliveryIdx {
    color: #46b476;
  }
`;
const PrintButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;

  svg path {
    fill: #ffffff;
  }
`;

const AGGREGATE_COLUMNS: TColumns[] = [
  { field: 'payRoomIdx', headerName: '결제번호', width: 300 },
  { field: 'createDate', headerName: '결제일시', width: 300, textAlign: 'center' },
  { field: 'count', headerName: '주문수량', width: 150, textAlign: 'center' },
  { field: 'price', headerName: '주문금액', width: 200, textAlign: 'end' }
];

const AGGREGATE_DESC = {
  BEFORE_DISPATCH: '*배차 성공 시, 주문이 확정처리됩니다. 제조 가능 여부 확인 후 “배차 요청하기” 버튼을 눌러주세요.',
  DISPATCHING: '배차가 완료될 때까지 잠시만 기다려주세요',
  AFTER_DISPATCH: '*배달 로봇 도착 시간에 맞춰 제조를 진행해 주세요',
  ERROR_DISPATCH: '잠시 후 다시 시도해 주세요'
};

interface OrderAggregateModalProps {
  open: boolean;
  isLabelPrintAvailable: boolean;
  checkedOrders: IConfirmOrder[];
  spot: IRobotSpot;
  onCancel: () => void;
  onConfirm: (dispatchData?: IRobotOrderSubscribe) => void;
}

const OrderAggregateModal = ({
  open,
  checkedOrders,
  spot,
  isLabelPrintAvailable,
  onCancel,
  onConfirm
}: OrderAggregateModalProps) => {
  const [robotDeliveryIdx, setRobotDeliveryIdx] = useState(null);
  const { selectedStore } = useMeInfo();
  const {
    mutate: saveOrderAggregate,
    isLoading,
    isError,
    reset,
    error: errorContent
  } = useRobotOrderDispatch((idx: number) => setRobotDeliveryIdx(idx));

  const dispatchUrl = `${getStoreHost()}/robot/v1/subscribe/${robotDeliveryIdx}`;
  const { data: dispatchData, loading, error } = useQuerySubscription(robotDeliveryIdx, dispatchUrl);

  useEffect(() => {
    if (!open) {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const dispathStatus = useMemo(() => {
    let status = 'BEFORE_DISPATCH';
    status = isLoading || (loading && !dispatchData) ? 'DISPATCHING' : status;
    status = !isLoading && !loading && dispatchData ? 'AFTER_DISPATCH' : status;
    status = error ? 'ERROR_SUBSRTIPTION' : status;
    status = isError ? 'ERROR_DISPATCH' : status;
    return status;
  }, [dispatchData, loading, isLoading, error, isError]);

  const errorMessage = useMemo(() => {
    if (
      errorContent?.response?.status === 500 ||
      errorContent?.response?.status === 400 ||
      errorContent?.response?.status === 401
    ) {
      return errorContent?.response?.data?.message;
    }
    if (error) {
      return `배차 정보를 불러오지 못했습니다.\n주문확정탭에서 배차 정보를 확인해주세요.`;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorContent, error]);

  const { tableData, totalCount, couponIds } = useMemo(() => {
    let menuCount = 0;
    const checkedCouponIds = [];

    const array = checkedOrders.map(({ couponId, menus, ...order }) => {
      checkedCouponIds.push(couponId);
      const count = menus.reduce((number, cur) => cur.count + number, 0);
      const price = menus.reduce((number, cur) => cur.count * cur.price + number, 0);
      menuCount += count;
      return { ...order, count, price: price.toLocaleString() };
    });
    return { tableData: array, totalCount: menuCount, couponIds: checkedCouponIds };
  }, [checkedOrders]);

  const dispatchTableData = useMemo(() => {
    return dispatchData?.details.map(({ paymentInfo, orderSummary }) => ({
      payRoomIdx: paymentInfo?.payRoomIdx,
      createDate: paymentInfo?.useDate,
      count: orderSummary?.totalOrderCount,
      price: orderSummary?.totalOrderAmount.toLocaleString()
    }));
  }, [dispatchData]);

  const handleAggregate = () => {
    if (dispathStatus === 'BEFORE_DISPATCH' || dispathStatus === 'ERROR_DISPATCH') {
      saveOrderAggregate({ couponIds });
    } else {
      setRobotDeliveryIdx(null);
      onConfirm(dispatchData);
    }
  };

  const handleCancel = () => {
    setRobotDeliveryIdx(null);
    onCancel?.();
  };

  const totalOrderCount = dispatchData?.totalOrderCount || tableData.length;
  const totalItemCount = dispatchData?.totalItemCount || totalCount;
  return (
    <DefaultModal
      open={open}
      okButtonProps={{
        style: {
          display: dispathStatus === 'ERROR_SUBSRTIPTION' ? 'none' : 'block',
          visibility: dispathStatus === 'DISPATCHING' ? 'hidden' : 'visible',
          transition: 'visibility 0s'
        }
      }}
      cancelButtonProps={{
        style: {
          display: dispathStatus === 'DISPATCHING' || dispathStatus === 'AFTER_DISPATCH' ? 'none' : 'block'
        }
      }}
      onOk={handleAggregate}
      onCancel={handleCancel}
      okText={
        dispathStatus === 'AFTER_DISPATCH' ? (
          <PrintButton>
            <span>{isLabelPrintAvailable ? '확인/출력' : '확인'}</span>
            {isLabelPrintAvailable ? <PrinterLightIcon /> : null}
          </PrintButton>
        ) : (
          <span>{dispathStatus === 'ERROR_DISPATCH' || dispathStatus === 'ERROR_SUBSRTIPTION' ? '재시도' : '배차 요청하기'}</span>
        )
      }
    >
      <AggregateModalWrapper direction="column" gap="25px">
        <FlexBoxFullWidthCenter justify="center">
          {dispathStatus === 'BEFORE_DISPATCH' ? (
            <ModalHeaderWrapper>
              <span>포함된 주문 건 확인 후&#32;</span>
              <span className="blue">배차 요청하기</span>
              <span>를 눌러주세요</span>
            </ModalHeaderWrapper>
          ) : null}
          {dispathStatus === 'DISPATCHING' ? <ModalHeaderWrapper>배차 요청 중</ModalHeaderWrapper> : null}
          {dispathStatus === 'ERROR_DISPATCH' ? <ModalHeaderWrapper className="red">배차 실패</ModalHeaderWrapper> : null}
          {dispathStatus === 'ERROR_SUBSRTIPTION' ? (
            <ModalHeaderWrapper style={{ color: '#46B476' }}>배차 완료</ModalHeaderWrapper>
          ) : null}
          {dispathStatus === 'AFTER_DISPATCH' ? (
            <ModalHeaderWrapper>
              <div style={{ color: '#46B476' }}>배차 완료</div>
              <div style={{ fontSize: '24px' }}>
                <span className="blue">{dispatchData?.deliveryRobot}&#32;</span>
                <span>배달로봇이&#32;</span>
                <span className="blue">{dispatchData?.arrivedMinute}분 후</span>
                <span>({dispatchData?.arrivedTime}) 출발지 도착 예정입니다</span>
              </div>
            </ModalHeaderWrapper>
          ) : null}
        </FlexBoxFullWidthCenter>
        {dispathStatus !== 'DISPATCHING' && dispathStatus !== 'ERROR_DISPATCH' && dispathStatus !== 'ERROR_SUBSRTIPTION' ? (
          <>
            <ModalContentWrapper>
              <FlexBoxFullWidthCenter justify="space-between">
                <div>출발지 : {selectedStore?.name}</div>
                {robotDeliveryIdx ? <div className="deliveryIdx">주문 승인 번호 : {robotDeliveryIdx}</div> : null}
              </FlexBoxFullWidthCenter>
              <FlexBoxFullWidthCenter justify="space-between">
                <div>목적지 : {spot?.spotName}</div>
                <div className="blue">
                  포함된 주문 : {totalOrderCount}건 ({totalItemCount}개)
                </div>
              </FlexBoxFullWidthCenter>
            </ModalContentWrapper>
            <DefaultTable columns={AGGREGATE_COLUMNS} data={dispatchTableData?.length ? dispatchTableData : tableData} />
          </>
        ) : null}
        {dispathStatus === 'DISPATCHING' ? <div className="loading-empty" /> : null}
        {dispathStatus === 'ERROR_DISPATCH' || dispathStatus === 'ERROR_SUBSRTIPTION' ? (
          <div className="loading-empty error">{errorMessage}</div>
        ) : null}
        <div className={`description ${dispathStatus === 'BEFORE_DISPATCH' ? 'red' : ''}`}>{AGGREGATE_DESC[dispathStatus]}</div>
      </AggregateModalWrapper>
    </DefaultModal>
  );
};

export default OrderAggregateModal;
